import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Add, CopyAll } from '@mui/icons-material';
import { Box, Button, MenuItem, Modal, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import useCurrentUser from '~/hooks/useCurrentUser';

import { ProgressStatusResponseMeta, getProgressStatuses, makeMeAnOffer } from '~/services/AccommodationService';

import asyncPoll from '~/utils/asyncPoll';

import MakeMeAnOfferCreateStatus, { MakeMeAnOfferCreateStatusProps } from './MakeMeAnOfferCreateStatus';

const MODAL_STYLE = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
  overflowY: 'auto',
};

type OfferType = 'hotel' | 'tactical_ao_hotel';
type OfferEnvironmentType = 'production' | 'staging';

interface MakeMeAnOffer {
  offerId?: string;
  offerEnvironment?: OfferEnvironmentType;
  offerType?: OfferType;
  name?: string;
  city?: string;
  country?: string;
  vendorId?: string;
  vendorEmail?: string;
}

enum MAKE_ME_AN_OFFER_SETUP_STEPS {
  NEW_OR_COPY_CONFIRMATION,
  COPY_OFFER_DETAILS,
  NEW_OFFER_DETAILS,
  OFFER_CONFIRMATION,
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MakeMeAnOfferModal({ open, onClose }: Props) {
  const { user } = useCurrentUser();

  const [currentStep, setCurrentStep] = useState<MAKE_ME_AN_OFFER_SETUP_STEPS>(
    MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OR_COPY_CONFIRMATION,
  );
  const [loading, setLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const makeMeAnOfferInitialValues: MakeMeAnOffer = {
    offerId: '',
    offerType: 'hotel',
    name: `${user?.fullName} test offer - DO NOT USE`,
    city: 'Bali',
    country: 'Indonesia',
    vendorId: '00128000009UXNYAA4',
    vendorEmail: user?.email,
  };

  const [makeMeAnOfferStatus, setMakeMeAnOfferStatus] = useState<MakeMeAnOfferCreateStatusProps['status']>({
    createOffer: 'awaiting',
    createProperty: 'awaiting',
    createPackages: 'awaiting',
    createSchedules: 'awaiting',
    createAvailability: 'awaiting',
  });

  const [offerUrl, setOfferUrl] = useState<string | null>(null);
  const [createdOfferName, setCreatedOfferName] = useState<string | null>(null);

  const { control, handleSubmit, watch } = useForm<MakeMeAnOffer>({
    mode: 'onBlur',
    defaultValues: makeMeAnOfferInitialValues,
  });

  const onModalClose = useCallback(() => {
    setOfferUrl(null);
    setCreatedOfferName(null);
    setMakeMeAnOfferStatus({
      createOffer: 'awaiting',
      createProperty: 'awaiting',
      createPackages: 'awaiting',
      createSchedules: 'awaiting',
      createAvailability: 'awaiting',
    });
    onClose();
  }, [onClose]);

  const name = watch('name');

  const handleSubmission = useCallback<SubmitHandler<MakeMeAnOffer>>(
    async (data) => {
      setLoading(true);

      const payload =
        currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.COPY_OFFER_DETAILS
          ? {
              offerId: data.offerId,
              offerEnvironment: data.offerEnvironment,
              vendorEmail: data.vendorEmail,
            }
          : {
              offerType: data.offerType,
              name: data.name,
              city: data.city,
              country: data.country,
              vendorId: data.vendorId,
              vendorEmail: data.vendorEmail,
            };

      const { status, result } = await makeMeAnOffer(payload);
      const jobId = result.id;

      try {
        await asyncPoll({
          apiCall: () => getProgressStatuses(jobId),
          validateFunction: async ({ result }) => {
            const makeTestOffer = result.find((status) => status.type === 'makeTestOffer');
            if (!makeTestOffer) {
              return false;
            }

            setMakeMeAnOfferStatus({
              createOffer: (makeTestOffer.meta as ProgressStatusResponseMeta).createOffer ?? 'awaiting',
              createProperty: (makeTestOffer.meta as ProgressStatusResponseMeta).createProperty ?? 'awaiting',
              createPackages: (makeTestOffer.meta as ProgressStatusResponseMeta).createPackages ?? 'awaiting',
              createSchedules: (makeTestOffer.meta as ProgressStatusResponseMeta).createSchedules ?? 'awaiting',
              createAvailability: (makeTestOffer.meta as ProgressStatusResponseMeta).createAvailability ?? 'awaiting',
            });

            if (makeTestOffer.state === 'completed') {
              setLoading(false);
              setOfferUrl(`/offers/${(makeTestOffer.meta as ProgressStatusResponseMeta).offerId}`);
              setCreatedOfferName((makeTestOffer.meta as ProgressStatusResponseMeta).offerName);
              return true;
            } else if (makeTestOffer.state === 'failed') {
              enqueueSnackbar('Failed to create offer', { variant: 'error' });
              setLoading(false);
              return true;
            } else {
              return false;
            }
          },
          maxTime: 300000, // 5 minutes
        });
      } catch (e) {
        // We need to continue process whether offer creation request failed or not
      }
    },
    [currentStep, enqueueSnackbar],
  );

  const handleCancel = useCallback(() => {
    setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OR_COPY_CONFIRMATION);
    onModalClose();
  }, [onModalClose]);

  useEffect(() => {
    setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OR_COPY_CONFIRMATION);
  }, []);

  return (
    <Modal open={open} onClose={onModalClose}>
      <Box sx={MODAL_STYLE}>
        <form onSubmit={handleSubmit(handleSubmission)}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Make me an offer
          </Typography>

          {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OR_COPY_CONFIRMATION && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CopyAll />}
                  onClick={() => setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.COPY_OFFER_DETAILS)}
                >
                  Copy offer details
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={() => setCurrentStep(MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OFFER_DETAILS)}
                >
                  New offer details
                </Button>
              </Stack>
            </Box>
          )}

          {(currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.COPY_OFFER_DETAILS ||
            currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OFFER_DETAILS) && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 9 }}>
                {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.COPY_OFFER_DETAILS && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                      <Typography variant="body2">Provide the offer ID of the offer you want to copy.</Typography>
                      <Typography variant="body2">
                        Offer environment determines where the existing offer details are fetched from.
                      </Typography>
                      <Typography variant="body2">
                        The offer must exist in public offer, otherwise we cannot fetch it.
                      </Typography>
                      <Typography variant="body2">
                        New offer will be created in staging with 2 years of availability at $100/night.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Controller
                        name="offerId"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Offer ID" fullWidth required sx={{ mb: 2 }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Controller
                        name="offerEnvironment"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Offer environment"
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                            helperText="Select the environment to fetch the existing offer details from."
                          >
                            <MenuItem value="production">Production</MenuItem>
                            <MenuItem value="staging">Staging</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                {currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OFFER_DETAILS && (
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Fill in the following form to make an offer in the staging environment.
                      </Typography>
                      <Typography variant="body2">
                        New offer will be created in staging with 2 years of availability at $100/night.
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Offer Name" fullWidth required sx={{ mb: 2 }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="offerType"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} select label="Offer Type" fullWidth required sx={{ mb: 2 }}>
                            <MenuItem value="hotel">Limited Time Lux Exclusive</MenuItem>
                            <MenuItem value="tactical_ao_hotel">Lux Premium Collection</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => <TextField {...field} label="City" fullWidth required sx={{ mb: 2 }} />}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="country"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Country" fullWidth required sx={{ mb: 2 }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="vendorId"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Vendor ID"
                            fullWidth
                            sx={{ mb: 2 }}
                            helperText="Amend if different vendor required"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="vendorEmail"
                        control={control}
                        render={({ field }) => <TextField {...field} label="Vendor Email" fullWidth sx={{ mb: 2 }} />}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <MakeMeAnOfferCreateStatus name={createdOfferName} status={makeMeAnOfferStatus} offerUrl={offerUrl} />
              </Grid>
            </Grid>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={handleCancel}>Cancel</Button>
                {(currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.COPY_OFFER_DETAILS ||
                  currentStep === MAKE_ME_AN_OFFER_SETUP_STEPS.NEW_OFFER_DETAILS) && (
                  <Button variant="contained" type="submit" disabled={loading}>
                    {loading ? 'Making offer...' : 'Make my offer'}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
}
